import React, { useEffect, useState } from 'react';
import {
    Grid,
    TextField,
    FormControlLabel,
    Switch,
    InputAdornment,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    Checkbox,
    ListItemText,
} from '@mui/material';
import { Title as TitleIcon, Description as DescriptionIcon } from '@mui/icons-material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSurveyStore } from '../../surveyutilities/surveyStore';

const FileUploadBlockProperties = ({ questionId }) => {
    const { getQuestionById, updateQuestion, validateName } = useSurveyStore();
    const question = getQuestionById(questionId);
    const [nameError, setNameError] = useState('');
    const [localName, setLocalName] = useState(question?.name || '');

    const fileTypeOptions = [
        'image/*',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'video/*',
        'audio/*'
    ];
    useEffect(() => {
        setLocalName(question?.name || '');
    }, [question?.name]);

    if (!question) return <Typography color="error">Question not found</Typography>;

    const handleUpdate = (property, value) => {
        updateQuestion(questionId, { [property]: value });
    };


    const handleNameChange = (value) => {
        setLocalName(value);
        const validation = validateName(value, questionId);
        setNameError(validation.error);

        if (validation.isValid) {
            updateQuestion(questionId, { name: value });
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Name */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Name (not visible to public, unique)"
                    value={localName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    error={!!nameError}
                    helperText={nameError}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <VisibilityOffIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>

            {/* Title */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Question Title"
                    value={question.title || ''}
                    onChange={(e) => handleUpdate('title', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Description */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={2}
                    value={question.description || ''}
                    onChange={(e) => handleUpdate('description', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Accepted File Types */}
           <Grid size={{xs:12}}>
                <FormControl fullWidth size="small">
                    <InputLabel>Accepted File Types</InputLabel>
                    <Select
                        multiple
                        value={question.acceptedFileTypes || []}
                        onChange={(e) => handleUpdate('acceptedFileTypes', e.target.value)}
                        label="Accepted File Types"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {fileTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                                <Checkbox checked={question.acceptedFileTypes?.includes(type) || false} />
                                <ListItemText primary={type} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* Max File Size */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Maximum File Size (MB)"
                    value={question.maxFileSize || 5}
                    onChange={(e) => handleUpdate('maxFileSize', parseInt(e.target.value) || 5)}
                    inputProps={{ min: 1, max: 100 }}
                    helperText="Maximum allowed file size in megabytes"
                />
            </Grid>

            {/* Allow Multiple Files */}
           <Grid size={{xs:12}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.allowMultiple || false}
                            onChange={(e) => handleUpdate('allowMultiple', e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Allow Multiple Files"
                />
            </Grid>

            {/* Required Switch */}
            <Grid size={{xs:12}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={(e) => handleUpdate('required', e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Required Question"
                />
            </Grid>

            {/* Question ID */}
            <Grid size={{xs:12}}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" gutterBottom>
                    ID: {questionId}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default FileUploadBlockProperties;